/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { ReactNode } from 'react';
import { useContext } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Link, breakpoints } from '@coursera/cds-core';
import { GlobeIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

type ExternalLinksItemProps = {
  icon?: ReactNode;
  text: string;
  url?: string;
};

const styles = {
  externalLink: () => css`
    display: flex;
    align-items: center;
    margin-bottom: var(--cds-spacing-100);

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  externalLinkText: () => css`
    margin-left: var(--cds-spacing-150);
  `,
};

export const ExternalLinksItem = ({ icon, text, url }: ExternalLinksItemProps) => {
  const { retrackedEventingData, sharedEventingV3Data } = useContext(ProfileContext);

  const retrackedTrack = useRetracked();
  const track = useTracker();
  const trackingData = {
    button: {
      name: EVENTS.openExternalLink,
      linkType: 'external' as const,
      linkURL: url,
      linkName: text,
    },
    ...sharedEventingV3Data,
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker(
    'view_button',
    trackingData
  );

  const trackClick = () => {
    retrackedTrack({
      trackingName: EVENTS.openExternalLink,
      action: 'click',
      trackingData: {
        ...retrackedEventingData,
        website: text,
      },
    });

    track('click_button', trackingData);
  };

  return (
    <TrackedDiv
      css={styles.externalLink}
      trackingName={EVENTS.openExternalLink}
      requireFullyVisible={false}
      withVisibilityTracking={true}
      data={{ ...retrackedEventingData, website: text }}
    >
      {icon || <GlobeIcon />}
      <Link
        href={url}
        onClick={trackClick}
        target="_blank"
        variant="standard"
        typographyVariant="bodyPrimary"
        css={styles.externalLinkText}
        ref={visibilityTrackingRef}
      >
        {text}
      </Link>
    </TrackedDiv>
  );
};

export default ExternalLinksItem;
